import type { ReactElement } from 'react';
import React from 'react';
import { Img, Link } from '@chakra-ui/react';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetIcon } from '../../../../components/icons/Icons';
import { Header, Text } from '../../../../ui-components';
import { useCustomPreferences } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { safeJsonParse } from '../../../../utils/safe-json-parse';

export const WhatCanYouGetSection = (): ReactElement => {
    const intl = useIntl();
    const customPreferences = useCustomPreferences([
        CustomPreference.GIFT_CARDS_WHAT_CAN_YOU_GET_LINKS,
    ]);

    const links = safeJsonParse(
        String(customPreferences[CustomPreference.GIFT_CARDS_WHAT_CAN_YOU_GET_LINKS]),
        {
            giftAnExperience: '/cooking-classes/',
            giftsUnder50: '/gifts-under-50/',
            giftsUnder100: '/gifts-under-100/',
            giftsThatWow: '/ultimate-gifts/',
        },
    );

    const greenCardData = {
        id: 'gift-an-experience',
        title: intl.formatMessage({
            id: 'gift_cards.what_can_you_get.gift_an_experience.title',
            defaultMessage: 'Gift an Experience',
        }),
        subtitle: intl.formatMessage({
            id: 'gift_cards.what_can_you_get.gift_an_experience.subtitle',
            defaultMessage: 'Cooking Classes from $70-$200+',
        }),
        image: getAssetUrl('static/img/gdp/gift-an-experience.jpg'),
        suggestedGiftCard: 'Suggested Gift Card Amounts for Cooking Classes:',
        singleClass: 'Single Class: $70-$130',
        twoSeats: 'Two Seats: $140-$260',
        workshops: 'Workshops: $200+',
        link: links.giftAnExperience,
    };

    const wineCardData = [
        {
            id: 'gifts-under-50',
            title: intl.formatMessage({
                id: 'gift_cards.what_can_you_get.gifts_under_50.title',
                defaultMessage: 'Gifts under $50',
            }),
            image: getAssetUrl('static/img/gdp/gifts-under-50.jpg'),
            link: links.giftsUnder50,
        },
        {
            id: 'gifts-under-100',
            title: intl.formatMessage({
                id: 'gift_cards.what_can_you_get.gifts_under_100.title',
                defaultMessage: 'Gifts under $100',
            }),
            image: getAssetUrl('static/img/gdp/gifts-under-100.jpg'),
            link: links.giftsUnder100,
        },
        {
            id: 'gifts-that-wow',
            title: intl.formatMessage({
                id: 'gift_cards.what_can_you_get.gifts_that_wow.title',
                defaultMessage: 'Gifts that WOW',
            }),
            image: getAssetUrl('static/img/gdp/gifts-that-wow.jpg'),
            link: links.giftsThatWow,
        },
    ];

    return (
        <div
            className="flex flex-col gap-8 rounded-none p-4 shadow-slt-cart-box sm:px-8 md:rounded-2xl"
            data-testid="gift-card-what-can-you-get-section"
        >
            <div className="flex flex-col gap-2 md:max-w-[538px] lg:max-w-[744px] xl:max-w-[964px]">
                <Header as="h3" variant="header3" style="bold" color="slt-pepper.1000">
                    <FormattedMessage
                        id="gift_cards.what_can_you_get.title"
                        defaultMessage="What Can You Get"
                    />
                </Header>
                <Text as="p" type="body" size="base" style="regular" color="slt-pepper.1000">
                    <FormattedMessage
                        id="gift_cards.what_can_you_get.description"
                        defaultMessage="<strong>Sur La Table gift cards allow your loved ones to dive into endless culinary possibilities!</strong> They can choose from a vast selection of kitchen tools gadgets, and premium cookware, or redeem the card for an unforgettable cooking class experience. Perfect for holidays birthdays, or any special occasion!"
                        values={{
                            strong: StrongText,
                        }}
                    />
                </Text>
            </div>
            <div className="flex flex-col gap-8 md:flex-row">
                <GreenCard {...greenCardData} />
                <div className="flex flex-col gap-4 md:grow md:flex-row">
                    {wineCardData.map((card) => (
                        <WineCard key={card.id} {...card} />
                    ))}
                </div>
            </div>
        </div>
    );
};
type GreenCardProps = {
    id: string;
    title: string;
    subtitle: string;
    image: string;
    suggestedGiftCard: string;
    singleClass: string;
    twoSeats: string;
    workshops: string;
    link: string;
};

const GreenCard = ({
    id,
    title,
    subtitle,
    image,
    suggestedGiftCard,
    singleClass,
    twoSeats,
    workshops,
    link,
}: GreenCardProps): ReactElement => (
    <Link
        href={link}
        className="text-decoration-none group w-full bg-[#34481D] shadow-slt-cart-box sm:rounded-2xl md:max-w-[269px] lg:max-w-[364px] xl:max-w-[482px]"
        data-testid={`green-card-${id}`}
        textDecoration="none"
        _hover={{
            textDecoration: 'none',
        }}
        title={title}
    >
        <div className="flex flex-col gap-1 px-4 py-6 md:py-4">
            <Header as="h4" variant="header4" style="bold" color="white">
                {title}
            </Header>
            <Text as="p" type="body" size="base" style="bold" color="white">
                {subtitle}
            </Text>
        </div>
        <div
            className="relative h-[278px] rounded-b-2xl bg-slt-black-500 bg-cover bg-center"
            style={{ backgroundImage: `url(${image})` }}
        >
            <div className="absolute inset-0 flex flex-col gap-2 rounded-b-2xl bg-[#34481D]/95 px-4 pb-4 pt-2 md:opacity-0 md:transition-opacity md:duration-300 md:ease-in-out md:group-hover:opacity-100">
                <Text as="p" type="body" size="base" style="bold" color="white">
                    {suggestedGiftCard}
                </Text>
                <div className="flex grow flex-col">
                    <Text as="p" type="body" size="base" style="bold" color="white">
                        {singleClass}
                    </Text>
                    <Text as="p" type="body" size="base" style="bold" color="white">
                        {twoSeats}
                    </Text>
                    <Text as="p" type="body" size="base" style="bold" color="white">
                        {workshops}
                    </Text>
                </div>
                <div className="flex justify-end">
                    <GetIcon id="external-link" name="external-link" size="lg" />
                </div>
            </div>
        </div>
    </Link>
);

type WineCardProps = {
    id: string;
    title: string;
    image: string;
    link: string;
};

const WineCard = ({ id, title, image, link }: WineCardProps): ReactElement => (
    <Link
        href={link}
        className="text-decoration-none group flex flex-row rounded-2xl bg-[#3D0B29] shadow-slt-cart-box md:w-1/3 md:flex-col-reverse"
        data-testid={`wine-card-${id}`}
        textDecoration="none"
        _hover={{
            textDecoration: 'none',
        }}
        title={title}
    >
        <div
            className="relative h-[148px] w-1/2 rounded-l-2xl rounded-br-none bg-slt-black-500 bg-cover bg-center sm:h-[207px] md:h-auto md:w-full md:grow md:rounded-b-2xl md:rounded-tl-none"
            style={{ backgroundImage: `url(${image})` }}
        >
            <Img
                src={getAssetUrl('static/img/gdp/up-hat.svg')}
                alt="up-hat"
                width="100%"
                height="fit-content"
                objectFit="cover"
                className="hidden md:block"
            />
            <div className="absolute inset-0 hidden items-end justify-end rounded-l-2xl rounded-br-none bg-[#3D0B29]/90 p-4 md:flex md:rounded-b-2xl md:rounded-tl-none md:opacity-0 md:transition-opacity md:duration-300 md:ease-in-out md:group-hover:opacity-100">
                <GetIcon id="external-link" name="external-link" size="lg" />
            </div>
        </div>
        <div className="flex w-1/2 flex-col gap-1 px-2 py-4 sm:px-4 sm:py-8 md:w-full md:py-4">
            <Header
                as="h4"
                variant="header4"
                style="bold"
                color="white"
                className="text-decoration-none grow text-left md:text-center"
                align={{
                    base: 'left',
                    md: 'center',
                }}
            >
                {title}
            </Header>
            <div className="flex justify-end md:hidden">
                <GetIcon id="external-link" name="external-link" size="lg" />
            </div>
        </div>
    </Link>
);

export const StrongText = (chunks: React.ReactNode[]): React.ReactElement => (
    <Text as="span" style="bold">
        {chunks}
    </Text>
);
